"use client";

/**
 * TeamDataDisplay - Team member data and shared resources
 */

import React, { useState } from "react";
import { Tag, Avatar, Tabs, TabList, Tab, TabPanel } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Groups,
  Person,
  Work,
  People,
  Folder,
  GroupOff,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface TeamMember {
  id: string | number;
  name: string;
  email?: string;
  avatar?: string;
  role?: string;
}

interface SharedResource {
  id: string | number;
  type: "candidate" | "contact" | "float" | "other";
  name: string;
  sharedBy?: string;
  sharedAt?: string;
  accessLevel?: "view" | "edit" | "admin";
}

interface TeamOutput {
  success?: boolean;
  team?: {
    id?: string;
    name?: string;
    members?: TeamMember[];
  };
  members?: TeamMember[];
  sharedResources?: SharedResource[];
  candidates?: SharedResource[];
  contacts?: SharedResource[];
  floats?: SharedResource[];
  message?: string;
  error?: string;
}

const resourceIcons: Record<string, React.ReactNode> = {
  candidate: <Person />,
  contact: <People />,
  float: <Work />,
  other: <Folder />,
};

function MemberItem({ member }: { member: TeamMember }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.sm,
        marginBottom: spacing[2],
        backgroundColor: colors.canvas.default,
        padding: spacing[3],
      }}
    >
      <Avatar src={member.avatar} name={member.name} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...textStyles.body1, color: colors.text.primary }}>
          {member.name}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          {member.email && (
            <span style={{ ...textStyles.caption, color: colors.text.muted }}>
              {member.email}
            </span>
          )}
          {member.role && (
            <Tag size="sm" color="neutral">
              {member.role}
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
}

function ResourceItem({ resource }: { resource: SharedResource }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.sm,
        marginBottom: spacing[2],
        backgroundColor: colors.canvas.default,
        padding: spacing[3],
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.action.productLight,
          color: colors.action.product,
          flexShrink: 0,
        }}
      >
        {resourceIcons[resource.type] || resourceIcons.other}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...textStyles.body1, color: colors.text.primary }}>
          {resource.name}
        </span>
        <div style={{ display: "flex", gap: spacing[2], alignItems: "center", marginTop: spacing[1] }}>
          <Tag size="sm" variant="default" color="neutral">
            {resource.type}
          </Tag>
          {resource.sharedBy && (
            <span style={{ ...textStyles.caption, color: colors.text.muted }}>
              by {resource.sharedBy}
            </span>
          )}
          {resource.accessLevel && (
            <Tag
              size="sm"
              color={resource.accessLevel === "admin" ? "danger" : resource.accessLevel === "edit" ? "warning" : "neutral"}
            >
              {resource.accessLevel}
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
}

export function TeamDataDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState("members");
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as TeamOutput | undefined;
  const members = result?.team?.members || result?.members || [];

  // Consolidate shared resources
  const allResources: SharedResource[] = [
    ...(result?.sharedResources || []),
    ...(result?.candidates?.map((c) => ({ ...c, type: "candidate" as const })) || []),
    ...(result?.contacts?.map((c) => ({ ...c, type: "contact" as const })) || []),
    ...(result?.floats?.map((f) => ({ ...f, type: "float" as const })) || []),
  ];

  const hasTabs = members.length > 0 && allResources.length > 0;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={result?.team?.name || "Team Data"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Groups style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading team data..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && (members.length > 0 || allResources.length > 0) && (
          <div>
            {/* Tabs if both members and resources exist */}
            {hasTabs ? (
              <Tabs value={activeTab} onChange={(v) => setActiveTab(v)}>
                <div style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                  <TabList>
                    <Tab value="members">{`Members (${members.length})`}</Tab>
                    <Tab value="shared">{`Shared (${allResources.length})`}</Tab>
                  </TabList>
                </div>

                <TabPanel value="members">
                  <div style={{ padding: spacing[4] }}>
                    {members.map((member) => (
                      <MemberItem key={member.id} member={member} />
                    ))}
                  </div>
                </TabPanel>

                <TabPanel value="shared">
                  <div style={{ padding: spacing[4] }}>
                    {allResources.map((resource, i) => (
                      <ResourceItem key={resource.id || i} resource={resource} />
                    ))}
                  </div>
                </TabPanel>
              </Tabs>
            ) : (
              <>
                {/* Members (no tabs) */}
                {members.length > 0 && (
                  <div style={{ padding: spacing[4] }}>
                    <ToolSectionHeader
                      icon={<Person style={{ fontSize: 18 }} />}
                      label={`Team Members (${members.length})`}
                    />
                    <div>
                      {members.map((member) => (
                        <MemberItem key={member.id} member={member} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Shared Resources (no tabs) */}
                {allResources.length > 0 && (
                  <div style={{ padding: spacing[4] }}>
                    <ToolSectionHeader
                      icon={<Folder style={{ fontSize: 18 }} />}
                      label={`Shared Resources (${allResources.length})`}
                    />
                    <div>
                      {allResources.map((resource, i) => (
                        <ResourceItem key={resource.id || i} resource={resource} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!isLoading && members.length === 0 && allResources.length === 0 && !isError && (
          <ToolEmptyState icon={<GroupOff />} message={result?.message || "No team data available"} />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default TeamDataDisplay;
